import React, { useMemo } from 'react';
import Modal from 'react-bootstrap/Modal';
import moment from 'moment';

const AttemptsHistoryModal = ({
    show,
    onClose,
    lesson,
    attempts = [],
    onOpenSummary,
    t
}) => {
    // 1. Calculate Summary Stats for this Lesson's Attempts
    const stats = useMemo(() => {
        if (!attempts || attempts.length === 0) {
            return {
                totalAttempts: 0,
                bestMark: 0,
                bestMax: 100,
                bestPct: 0,
                avgPct: 0,
                hasAnyResult: false
            };
        }

        let totalAttempts = attempts.length;
        let validScores = [];
        let bestPct = 0;
        let bestMark = 0;
        let bestMax = 100;

        attempts.forEach(att => {
            if (att.Mark !== null && att.Mark !== undefined) {
                const m = Number(att.Mark);
                const maxM = Number(att.TotalMark || lesson?.TotalMark || 100);
                const pct = maxM > 0 ? Math.round((m / maxM) * 100) : 0;
                validScores.push(pct);

                if (pct >= bestPct) {
                    bestPct = pct;
                    bestMark = m;
                    bestMax = maxM;
                }
            }
        });

        const avgPct = validScores.length > 0
            ? Math.round(validScores.reduce((sum, val) => sum + val, 0) / validScores.length)
            : 0;

        return {
            totalAttempts,
            bestMark,
            bestMax,
            bestPct,
            avgPct,
            hasAnyResult: validScores.length > 0
        };
    }, [attempts, lesson]);

    if (!show || !lesson) return null;

    return (
        <Modal
            size="lg"
            centered
            show={show}
            onHide={onClose}
            backdrop="static"
            enforceFocus={false}
            dialogClassName="modal-attempts-history"
        >
            {/* 🌟 Modern Modal Header matching Mission Statistics */}
            <Modal.Header closeButton className="bg-white border-bottom shadow-sm px-3 px-md-4 py-3">
                <Modal.Title className="d-flex align-items-center w-100 justify-content-between">
                    <div className="d-flex align-items-center">
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center ml-3 shadow-xs"
                            style={{
                                width: '44px',
                                height: '44px',
                                minWidth: '44px',
                                backgroundColor: '#eef2ff',
                                color: '#4f46e5',
                                fontSize: '1.25rem'
                            }}
                        >
                            <i className="fad fa-history"></i>
                        </div>
                        <div>
                            <div className="d-flex align-items-center flex-wrap gap-2">
                                <h5 className="font-weight-bold mb-0 text-dark" style={{ fontSize: '1.08rem' }}>
                                    {t('Pages.Lessons.AttemptsHistory') || 'سِجلّ المحاولات'}
                                </h5>
                                <span
                                    className="badge badge-primary px-2 py-1 mr-2"
                                    style={{
                                        borderRadius: '6px',
                                        fontSize: '0.78rem',
                                        backgroundColor: '#4f46e5',
                                        width: 'auto'
                                    }}
                                >
                                    {attempts.length} {t('Pages.Lessons.Attempts') || 'محاولات'}
                                </span>
                            </div>
                            <div className="small text-muted d-flex align-items-center mt-1 flex-wrap">
                                <span className="font-weight-bold text-primary">{lesson.Name || lesson.title}</span>
                                {stats.hasAnyResult && (
                                    <>
                                        <span className="mx-2 text-muted">•</span>
                                        <span>
                                            {t('Pages.Lessons.HighestScore') || 'أعلى نسبة'}:{' '}
                                            <strong className="text-success">%{stats.bestPct}</strong>
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Quick Stats Chips (Top Right) */}
                    {stats.hasAnyResult && (
                        <div className="d-none d-sm-flex align-items-center ml-3" style={{ gap: '8px' }}>
                            <span
                                className="status-chip success d-inline-flex align-items-center py-1 px-2"
                                style={{
                                    backgroundColor: stats.bestPct >= 80 ? '#dcfce7' : stats.bestPct >= 60 ? '#fef3c7' : '#fee2e2',
                                    color: stats.bestPct >= 80 ? '#16a34a' : stats.bestPct >= 60 ? '#d97706' : '#dc2626',
                                    borderRadius: '8px',
                                    fontSize: '0.76rem',
                                    fontWeight: 'bold',
                                    width: 'auto'
                                }}
                            >
                                <i className="fas fa-trophy ml-1"></i>
                                <span>%{stats.bestPct} {t('Pages.Lessons.HighestScore') || 'أفضل نتيجة'}</span>
                            </span>
                            <span
                                className="status-chip primary d-inline-flex align-items-center py-1 px-2"
                                style={{
                                    backgroundColor: '#e0e7ff',
                                    color: '#4338ca',
                                    borderRadius: '8px',
                                    fontSize: '0.76rem',
                                    fontWeight: 'bold',
                                    width: 'auto'
                                }}
                            >
                                <i className="fas fa-calculator ml-1"></i>
                                <span>%{stats.avgPct} {t('Pages.Lessons.AverageScore') || 'المعدل'}</span>
                            </span>
                        </div>
                    )}
                </Modal.Title>
            </Modal.Header>

            {/* 🌟 Modern Scrollable Body */}
            <Modal.Body className="p-3 p-md-4" style={{ backgroundColor: '#f8fafc', maxHeight: 'calc(80vh - 120px)', overflowY: 'auto' }}>
                {attempts.length === 0 ? (
                    <div className="theme-card summary-card p-5 text-center bg-white shadow-sm rounded-lg my-3">
                        <div className="display-4 text-warning mb-3">
                            <i className="fal fa-file-search"></i>
                        </div>
                        <h5 className="font-weight-bold text-dark mb-1">
                            {t('Pages.Lessons.NoAttemptsYet') || 'لا توجد محاولات مسجلة لهذا الدرس.'}
                        </h5>
                        <p className="text-muted small mb-0">
                            {t('Pages.Lessons.NoMarksPeriod') || 'قم بحل الدرس لتسجيل الدرجات والمهارات والمحاولات'}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* 1. Mini KPI Cards Row */}
                        <div className="row mb-3" style={{ margin: '-4px' }}>
                            {/* Card 1: Total Attempts */}
                            <div className="col-4 p-1">
                                <div
                                    className="bg-white border rounded p-2 p-md-3 shadow-xs d-flex align-items-center"
                                    style={{ borderRadius: '12px' }}
                                >
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center ml-2 ml-md-3"
                                        style={{
                                            width: '38px',
                                            height: '38px',
                                            minWidth: '38px',
                                            backgroundColor: '#eef2ff',
                                            color: '#4f46e5',
                                            fontSize: '1.1rem'
                                        }}
                                    >
                                        <i className="fas fa-history"></i>
                                    </div>
                                    <div className="overflow-hidden">
                                        <div className="text-muted small" style={{ fontSize: '0.72rem', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                            {t('Pages.Lessons.TotalLessons') || 'إجمالي المحاولات'}
                                        </div>
                                        <div className="font-weight-bold text-dark" style={{ fontSize: '1.1rem' }}>
                                            {attempts.length}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Card 2: Highest Score */}
                            <div className="col-4 p-1">
                                <div
                                    className="bg-white border rounded p-2 p-md-3 shadow-xs d-flex align-items-center"
                                    style={{ borderRadius: '12px' }}
                                >
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center ml-2 ml-md-3"
                                        style={{
                                            width: '38px',
                                            height: '38px',
                                            minWidth: '38px',
                                            backgroundColor: '#dcfce7',
                                            color: '#16a34a',
                                            fontSize: '1.1rem'
                                        }}
                                    >
                                        <i className="fas fa-award"></i>
                                    </div>
                                    <div className="overflow-hidden">
                                        <div className="text-muted small" style={{ fontSize: '0.72rem', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                            {t('Pages.Lessons.HighestScore') || 'أعلى نسبة'}
                                        </div>
                                        <div className="font-weight-bold text-success" style={{ fontSize: '1.1rem' }}>
                                            %{stats.bestPct}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Card 3: Average Score */}
                            <div className="col-4 p-1">
                                <div
                                    className="bg-white border rounded p-2 p-md-3 shadow-xs d-flex align-items-center"
                                    style={{ borderRadius: '12px' }}
                                >
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center ml-2 ml-md-3"
                                        style={{
                                            width: '38px',
                                            height: '38px',
                                            minWidth: '38px',
                                            backgroundColor: '#fef3c7',
                                            color: '#d97706',
                                            fontSize: '1.1rem'
                                        }}
                                    >
                                        <i className="fas fa-chart-line"></i>
                                    </div>
                                    <div className="overflow-hidden">
                                        <div className="text-muted small" style={{ fontSize: '0.72rem', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                            {t('Pages.Lessons.AverageScore') || 'معدل الدرجات'}
                                        </div>
                                        <div className="font-weight-bold text-warning" style={{ fontSize: '1.1rem' }}>
                                            %{stats.avgPct}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 2. Interactive Modern Table */}
                        <div
                            className="bg-white shadow-sm border rounded overflow-hidden"
                            style={{ borderRadius: '12px' }}
                        >
                            <div className="table-responsive mb-0">
                                <table className="table table-hover align-middle mb-0 text-right" style={{ fontSize: '0.86rem' }}>
                                    <thead style={{ backgroundColor: '#f1f5f9', color: '#475569', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        <tr>
                                            <th className="text-center" style={{ width: '65px' }}>
                                                <i className="fas fa-hashtag text-muted ml-1"></i>
                                                <span>{t('Pages.Lessons.Attempt') || 'المحاولة'}</span>
                                            </th>
                                            <th>
                                                <i className="far fa-calendar-alt text-primary ml-1"></i>
                                                <span>{t('Pages.Lessons.DateAndTiming') || 'تاريخ وتوقيت الحل'}</span>
                                            </th>
                                            <th>
                                                <i className="fas fa-award text-success ml-1"></i>
                                                <span>{t('Pages.Lessons.AchievedMark') || 'العلامة المحرزة'}</span>
                                            </th>
                                            <th>
                                                <i className="fas fa-percentage text-info ml-1"></i>
                                                <span>{t('Pages.Lessons.MasteryPercentage') || 'نسبة الإتقان'}</span>
                                            </th>
                                            <th>
                                                <i className="far fa-clock text-warning ml-1"></i>
                                                <span>{t('Pages.Lessons.SolveDuration') || 'وقت الحل'}</span>
                                            </th>
                                            <th className="text-center">
                                                <i className="fas fa-sliders-h text-secondary ml-1"></i>
                                                <span>{t('Pages.Lessons.ResultDetails') || 'تفاصيل النتائج'}</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {attempts.map((att, aIdx) => {
                                            const attMax = Number(att.TotalMark || lesson.TotalMark || 100);
                                            const hasResult = att.Mark !== null && att.Mark !== undefined;
                                            const attMark = hasResult ? Number(att.Mark) : 0;
                                            const attPct = (hasResult && attMax > 0) ? Math.round((attMark / attMax) * 100) : 0;

                                            const attClock = att.UserTime?.Clock;
                                            let attDuration = '-';
                                            if (attClock) {
                                                const ah = attClock.hour ? `${attClock.hour}:` : '';
                                                const am = attClock.minutes >= 10 ? attClock.minutes : `0${attClock.minutes || 0}`;
                                                const as = attClock.seconds >= 10 ? attClock.seconds : `0${attClock.seconds || 0}`;
                                                attDuration = `${ah}${am}:${as}`;
                                            } else if (att.UserTime?.duration) {
                                                attDuration = `${att.UserTime.duration} ث`;
                                            }

                                            const attemptNum = attempts.length - aIdx;
                                            const isLatest = aIdx === 0;
                                            const dateStr = att.CreationDate
                                                ? moment(att.CreationDate).format('YYYY-MM-DD HH:mm')
                                                : '-';

                                            let statusColor = '#ef4444';
                                            let statusBg = '#fee2e2';
                                            let statusLabel = t('Pages.Lessons.ScoreNeedsImprovement') || 'بحاجة لتحسين';

                                            if (attPct >= 90) {
                                                statusColor = '#16a34a';
                                                statusBg = '#dcfce7';
                                                statusLabel = `${t('Pages.Lessons.ScoreExcellent') || 'ممتاز'} 🌟`;
                                            } else if (attPct >= 75) {
                                                statusColor = '#4f46e5';
                                                statusBg = '#eef2ff';
                                                statusLabel = `${t('Pages.Lessons.ScoreVeryGood') || 'جيد جداً'} 👍`;
                                            } else if (attPct >= 60) {
                                                statusColor = '#d97706';
                                                statusBg = '#fef3c7';
                                                statusLabel = `${t('Pages.Lessons.ScoreGood') || 'متوسط'} ⚡`;
                                            }

                                            return (
                                                <tr
                                                    key={att.Id || aIdx}
                                                    style={{
                                                        backgroundColor: isLatest ? 'rgba(238, 242, 255, 0.35)' : 'transparent',
                                                        transition: 'background-color 0.2s ease'
                                                    }}
                                                >
                                                    {/* Attempt Number */}
                                                    <td className="text-center" style={{ verticalAlign: 'middle' }}>
                                                        <span
                                                            className={`badge ${isLatest ? 'badge-primary' : 'badge-light border text-muted'} px-2 py-1`}
                                                            style={{ borderRadius: '6px', fontSize: '0.78rem', width: 'auto' }}
                                                        >
                                                            #{attemptNum}
                                                        </span>
                                                    </td>

                                                    {/* Date & Time */}
                                                    <td style={{ verticalAlign: 'middle' }}>
                                                        <div className="d-flex flex-column">
                                                            <span className="font-weight-bold text-dark">{dateStr}</span>
                                                            {isLatest && (
                                                                <span className="text-primary font-weight-bold small" style={{ fontSize: '0.70rem' }}>
                                                                    <i className="fas fa-star text-warning ml-1"></i>
                                                                    {t('Pages.Lessons.LatestAttempt') || 'المحاولة الأحدث'}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>

                                                    {/* Mark Achieved */}
                                                    <td style={{ verticalAlign: 'middle' }}>
                                                        {hasResult ? (
                                                            <div className="d-flex align-items-center">
                                                                <span className="font-weight-bold text-dark ml-1">
                                                                    {attMark} / {attMax}
                                                                </span>
                                                                <span className="text-muted small">{t('Pages.Lessons.Mark') || 'علامة'}</span>
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>

                                                    {/* Percentage & Progress Bar */}
                                                    <td style={{ verticalAlign: 'middle', minWidth: '130px' }}>
                                                        {hasResult ? (
                                                            <div className="d-flex flex-column">
                                                                <div className="d-flex justify-content-between align-items-center small mb-1">
                                                                    <span
                                                                        className="status-chip py-0 px-2 small font-weight-bold"
                                                                        style={{
                                                                            backgroundColor: statusBg,
                                                                            color: statusColor,
                                                                            borderRadius: '6px',
                                                                            fontSize: '0.72rem',
                                                                            width: 'auto'
                                                                        }}
                                                                    >
                                                                        {statusLabel}
                                                                    </span>
                                                                    <span className="font-weight-bold" style={{ color: statusColor }}>
                                                                        %{attPct}
                                                                    </span>
                                                                </div>
                                                                <div style={{ height: '5px', backgroundColor: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                                                                    <div
                                                                        style={{
                                                                            width: `${attPct}%`,
                                                                            backgroundColor: statusColor,
                                                                            height: '100%'
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="badge badge-light border text-muted px-2 py-1" style={{ borderRadius: '6px', width: 'auto' }}>
                                                                {t('Pages.Lessons.NoMarkRecorded') || 'لم تسجل علامة'}
                                                            </span>
                                                        )}
                                                    </td>

                                                    {/* Duration */}
                                                    <td style={{ verticalAlign: 'middle' }}>
                                                        <span className="text-muted d-inline-flex align-items-center">
                                                            <i className="far fa-clock ml-1 text-muted"></i>
                                                            {attDuration}
                                                        </span>
                                                    </td>

                                                    {/* Action Button: Summary / Solves */}
                                                    <td className="text-center" style={{ verticalAlign: 'middle' }}>
                                                        {att.HasLessonSolve ? (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-primary shadow-xs px-3 py-1 font-weight-bold d-inline-flex align-items-center"
                                                                style={{
                                                                    borderRadius: '8px',
                                                                    fontSize: '0.78rem',
                                                                    backgroundColor: '#4f46e5',
                                                                    borderColor: '#4f46e5',
                                                                    width: 'auto'
                                                                }}
                                                                onClick={() => {
                                                                    onClose();
                                                                    if (onOpenSummary) onOpenSummary(lesson, att);
                                                                }}
                                                                title={t('Pages.Lessons.ViewSummary') || 'عرض تفاصيل نتائج الدرس ومؤشرات المهارات'}
                                                            >
                                                                <i className="fad fa-chart-pie ml-1"></i>
                                                                <span>{t('Pages.Lessons.ViewSummary') || 'نتائج المهارات'}</span>
                                                            </button>
                                                        ) : (
                                                            <span className="badge badge-light border text-muted px-2 py-1 small" style={{ borderRadius: '6px', width: 'auto' }}>
                                                                {t('Pages.Lessons.NoSolvesRecorded') || 'لا توجد إجابات'}
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </Modal.Body>

            {/* 🌟 Modern Modal Footer */}
            <Modal.Footer className="border-top py-3 px-4 justify-content-between bg-white">
                <div className="small text-muted d-flex align-items-center">
                    <i className="fas fa-info-circle ml-1 text-primary"></i>
                    <span>{t('Pages.Lessons.AttemptsHistoryDesc') || 'استعراض تفاصيل كافة المحاولات السابقة والدرجات المحرزة'}</span>
                </div>
                <button
                    type="button"
                    className="btn btn-secondary btn-sm px-4 py-2 font-weight-bold shadow-xs"
                    style={{ borderRadius: '8px', width: 'auto' }}
                    onClick={onClose}
                >
                    {t('cancel') || 'إغلاق'}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default AttemptsHistoryModal;
